import { h } from "preact";
import { Text } from "preact-i18n";
import { useEffect, useRef, useState } from "preact/hooks";
import "../../style/ImageLightbox.css";

const ImageLightbox = ({ imageUrl, imageName, isOpen, onClose, onDownload }) => {
  const [imageError, setImageError] = useState(false);
  const closeButtonRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setImageError(false);
    }
  }, [isOpen, imageUrl]);

  useEffect(() => {
    if (!isOpen) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    closeButtonRef.current?.focus();

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className="image-lightbox-overlay"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-label={imageName ? `Enlarged image: ${imageName}` : "Enlarged image"}
    >
      <div className="image-lightbox-toolbar">
        <button className="image-lightbox-button" onClick={onDownload} aria-label="Download image">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            fill="none"
            viewBox="0 0 24 24"
            stroke-width="2"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3"
            />
          </svg>
        </button>
        <button
          className="image-lightbox-button"
          onClick={onClose}
          aria-label="Close lightbox"
          ref={closeButtonRef}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            fill="none"
            viewBox="0 0 24 24"
            stroke-width="2"
            stroke="currentColor"
          >
            <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      {imageError ? (
        <div className="image-lightbox-error">
          <div className="image-lightbox-error-heading">
            <Text id="IMAGE_UNAVAILABLE">Image unavailable</Text>
          </div>
          <div className="image-lightbox-error-message">
            <Text id="IMAGE_LOAD_ERROR">
              We couldn't load the full-size image. Please try again.
            </Text>
          </div>
        </div>
      ) : (
        <img
          className="image-lightbox-image"
          src={imageUrl}
          alt="Invoice item image"
          onError={() => setImageError(true)}
        />
      )}
    </div>
  );
};

export default ImageLightbox;
